// Componente de Onboarding v3 - 5 Etapas Principais
// Novo fluxo: Empresa → Cliente → Orçamento → PDF → Pedido
// Com compatibilidade total para usuários legados (v1)

import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, X, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import {
  getOnboardingProgress,
  completeOnboardingStep,
} from "@/integrations/supabase/onboarding";
import {
  ONBOARDING_STEPS,
  BASIC_ONBOARDING_STEPS,
  STEP_CONFIG,
  type OnboardingStep,
} from "@/constants/onboarding";
import { listOrders } from "@/integrations/supabase/orders";
import { listQuotes } from "@/integrations/supabase/quotes";
import { EvolvementCard } from "@/components/EvolvementCard";

export function OnboardingChecklist() {
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const autoMarkDoneRef = useRef(false);

  // Buscar progresso do onboarding
  const { data: onboardingProgress, refetch: refetchProgress } = useQuery({
    queryKey: ["onboarding-progress", empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
    staleTime: 10000,
  });

  // Buscar dados para detecção automática de etapas
  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("id");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: orders = [] } = useQuery({
    queryKey: ["orders"],
    queryFn: listOrders,
  });

  const { data: quotes = [] } = useQuery({
    queryKey: ["quotes"],
    queryFn: listQuotes,
  });

  // Auto-marcar etapas baseado em dados (apenas uma vez por empresa)
  useEffect(() => {
    if (!empresa?.id || !onboardingProgress) return;
    if (autoMarkDoneRef.current) return;

    const markStepsAutomatically = async () => {
      try {
        // Etapa: Primeiro cliente
        if (customers.length > 0) {
          await completeOnboardingStep(empresa.id, ONBOARDING_STEPS.FIRST_CLIENT);
        }

        // Etapa: Primeiro orçamento
        if (quotes.length > 0) {
          await completeOnboardingStep(empresa.id, ONBOARDING_STEPS.FIRST_QUOTE);
        }

        // Etapa: Primeiro pedido (ordem)
        if (orders.length > 0) {
          await completeOnboardingStep(empresa.id, ONBOARDING_STEPS.FIRST_ORDER);
        }

        // Refetch após marcar
        refetchProgress();
      } finally {
        autoMarkDoneRef.current = true;
      }
    };

    markStepsAutomatically();
  }, [empresa?.id]);

  // Controlar visibilidade baseado no progresso
  useEffect(() => {
    if (!onboardingProgress) return;

    // Se é usuário legado ou completou tudo, mostrar EvolvementCard
    if (onboardingProgress.isLegacyUser || onboardingProgress.shouldShowEvolvementCard) {
      setIsVisible(false);
      return;
    }

    // Se deve mostrar checklist
    if (onboardingProgress.shouldShowChecklist) {
      setIsVisible(true);
      return;
    }

    setIsVisible(false);
  }, [onboardingProgress]);

  // Se deve mostrar EvolvementCard (após conclusão básica)
  if (onboardingProgress?.shouldShowEvolvementCard && !isVisible) {
    return <EvolvementCard />;
  }

  if (!isVisible || !onboardingProgress) {
    return null;
  }

  const stepsToShow = BASIC_ONBOARDING_STEPS.map(step => ({
    id: step,
    title: STEP_CONFIG[step].title,
    description: STEP_CONFIG[step].description,
    route: STEP_CONFIG[step].route,
    completed: onboardingProgress.steps?.some(s => s.step === step && s.completed) || false,
  }));

  const allCompleted = stepsToShow.every(s => s.completed);
  const progress = onboardingProgress.percentage;

  const handleStepClick = async (step: any) => {
    // Etapa especial: visualizar PDF do orçamento
    if (step.id === ONBOARDING_STEPS.QUOTE_PDF_VIEWED) {
      // Navegar para página de orçamentos (listagem)
      navigate("/orcamentos");
      return;
    }

    // Etapa especial: primeiro pedido (aprovar orçamento)
    if (step.id === ONBOARDING_STEPS.FIRST_ORDER) {
      // Navegar para página de orçamentos para aprovar
      navigate("/orcamentos");
      return;
    }

    // Outras etapas: usar rota definida
    if (step.route) {
      navigate(step.route);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  return (
    <Card className="mb-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg animate-border-shine">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-lg font-bold text-gray-900">
              🎉 Bem-vindo ao Ateliê Pro!
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Complete estes passos para começar a usar o app:
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Barra de progresso */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Progresso</span>
            <span className="font-semibold text-purple-600">
              {onboardingProgress.completed}/{onboardingProgress.total} concluído
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 text-right">{Math.round(progress)}% concluído</p>
        </div>

        {/* Lista de steps */}
        <div className="space-y-2">
          {stepsToShow.map((step) => (
            <button
              key={step.id}
              onClick={() => handleStepClick(step)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                step.completed
                  ? "bg-green-50 border-green-200 hover:bg-green-100"
                  : "bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50 cursor-pointer"
              }`}
              disabled={step.completed}
            >
              <div className={`flex-shrink-0 ${step.completed ? "text-green-600" : "text-gray-400"}`}>
                {step.completed ? (
                  <CheckCircle2 className="h-6 w-6 animate-pulse" />
                ) : (
                  <Circle className="h-6 w-6" />
                )}
              </div>
              <div className="flex-1 text-left">
                <div className={`font-medium text-sm ${step.completed ? "text-green-900" : "text-gray-900"}`}>
                  {step.title}
                </div>
                <div className="text-xs text-gray-600">{step.description}</div>
              </div>
              {!step.completed && step.route && (
                <ArrowRight className="h-4 w-4 text-purple-600 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>

        {/* Mensagem de encorajamento */}
        {onboardingProgress.completed > 0 && !allCompleted && (
          <div className="pt-2 text-center">
            <p className="text-sm text-purple-600 font-medium">
              🎯 Continue assim! Você está no caminho certo!
            </p>
          </div>
        )}

        {allCompleted && (
          <div className="pt-2 text-center">
            <p className="text-sm font-bold text-green-600">
              ✅ Parabéns! Você completou o onboarding básico!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
